import {
  SpreadState,
  FinancialValue,
  PerformanceState,
  RiskState,
  BessMetadata,
  WarrantyState,
  SafetyState,
} from '@twaice-fe/shared/models';
import { CriticalSeverityColors, ModerateSeverityColors, StableSeverityColors } from '@twaice-fe/frontend/shared/utilities';
import { NzTableFilterFn, NzTableFilterList, NzTableSortFn, NzTableSortOrder } from 'ng-zorro-antd/table';

/** Ordered. Absent means we hold no reading for the status. */
export enum StatusSeverity {
  Normal = 0,
  Medium = 1,
  Critical = 2,
}

export interface StatsValuesConfigInterface {
  label: string | null;
  title?: string;
  color: string;
  severity?: StatusSeverity;
  value?: number;
  icon?: string;
  link?: string;
  isFeatureUnavailable?: boolean;
}

export interface ColumnItem<T> {
  name: string;
  tooltip?: {
    title?: string;
    content: string;
    link?: string;
    linkText?: string;
  };
  class?: string;
  width?: string;
  sortOrder?: NzTableSortOrder | null;
  sortFn?: NzTableSortFn<T> | null;
  sortDirections?: NzTableSortOrder[];
  filters?: NzTableFilterList;
  filterFn?: NzTableFilterFn<T> | null;
}

export interface SystemOverviewAntTableInterface {
  customerBk: string;
  systemBk: string;
  id: string;
  systemName?: string;
  bessMetadata?: BessMetadata;
  storageLocation: string;
  performance?: StatsValuesConfigInterface;
  safetyScore?: StatsValuesConfigInterface;
  rte?: StatsValuesConfigInterface;
  risk?: StatsValuesConfigInterface;
  warrantyState?: StatsValuesConfigInterface;
  usableEnergyPercentage?: number | null;
  recoverableEnergyMWh?: number | null;
  pricePerMwh?: FinancialValue | null;
  newChatMessagesAvailable: boolean;
}

export interface ColumnVisibility {
  isRTESummaryEnabled: boolean;
  isPenaltyRiskEnabled: boolean;
}

export interface SystemPortfolioOverviewViewModel extends ColumnVisibility {
  columns: ColumnItem<SystemOverviewAntTableInterface>[];
  rows: SystemOverviewAntTableInterface[];
  isFinancialImpactDisabled: boolean;
}

export const PERFORMANCE_STATUS_COLUMN: ColumnItem<SystemOverviewAntTableInterface> = {
  name: 'Performance Status',
  class: 'justify-center',
  tooltip: {
    link: 'https://help.twaice.cloud/en/articles/277859-optimize-performance-with-usable-and-recoverable-energy',
    linkText: 'Learn more about the usable energy and daily loss',
    content:
      'This metric tracks the loss in energy and revenue due to the difference between current usable energy and initial energy of the storage.' +
      ' Calculated based on one cycle per day, with no consideration of aging.' +
      ' If strings within one node can be operated independently, actual losses might be lower.' +
      ' Revenue loss is based on an market specific benchmark. Use this insight to optimize performance.',
  },
};

export const ANOMALIES_COLUMN: ColumnItem<SystemOverviewAntTableInterface> = {
  name: 'Anomalies',
  tooltip: {
    link: 'https://help.twaice.cloud/en/articles/211666-twaice-safety-index',
    linkText: 'Learn more about Anomalies',
    content:
      'The index reflects the current safety status of the storage, ranging from ‘Stable’, to ‘Action Recommended’ to ‘Action Required’. It is calculated based on the number of anomalous measurements for temperature and resistance. Use this to prioritize maintenance and reduce risks.',
  },
  class: 'justify-center',
};

export const PENALTY_RISK_COLUMN: ColumnItem<SystemOverviewAntTableInterface> = {
  name: 'Penalty Risk',
  tooltip: {
    title: 'Penalty & Derating Risk',
    link: 'https://help.twaice.cloud/en/articles/307824-how-to-use-penalty-risk-assessment',
    linkText: 'Learn more about how risk is calculated',
    content:
      'This page helps you understand why your system may be at risk of power penalties. Start by reviewing how your system has been operating—how far the BMS SoC differs from actual usable SoC, and whether daily SoC levels stay within a safe range.Use the table to identify the most critical components that may be contributing to risk. For a deeper dive into actual power breaches and the strings involved, head to the Insights tab.',
  },
  class: 'justify-center',
};

export const WARRANTY_STATUS_COLUMN: ColumnItem<SystemOverviewAntTableInterface> = {
  name: 'Warranty Status',
  tooltip: {
    link: 'https://help.twaice.cloud/en/articles/250404-how-to-use-the-warranty-guarantee-tracker',
    linkText: 'Learn more about the Warranty & Guarantee Tracker',
    content:
      'This is the current warranty & guarantee tracker status of the system. If any of the configured thresholds were breached within the last 2 weeks, the status is shown as breached.' +
      ' If no KPIs are configured, the status is shown as "Onboarding in Progress". If no thresholds are configures the status is always shown as "Operation within conditions".',
  },
  class: 'justify-center',
};

export const RTE_SUMMARY_COLUMN: ColumnItem<SystemOverviewAntTableInterface> = {
  name: 'Round Trip Efficiency',
  tooltip: {
    link: 'https://help.twaice.cloud/en/articles/277881-track-round-trip-efficiency-rte-on-system-level',
    linkText: 'Learn more about the Round Trip Efficiency',
    content:
      'The system AC Roundtrip Efficiency (RTE) is calculated based on an aggregation of the battery, inverters and transformers efficiencies.',
  },
  class: 'justify-center',
};

export const STATUS_CONFIGS: Record<string, Record<string, StatsValuesConfigInterface>> = {
  safety: {
    [SafetyState.ACTION_REQUIRED]: {
      label: 'Action Required',
      color: CriticalSeverityColors.accent,
      severity: StatusSeverity.Critical,
    },
    [SafetyState.ACTION_RECOMMENDED]: {
      label: 'Action Recommended',
      color: ModerateSeverityColors.accent,
      severity: StatusSeverity.Medium,
    },
    [SafetyState.STABLE]: { label: 'Stable', color: StableSeverityColors.accent, severity: StatusSeverity.Normal },
  },
  performance: {
    [PerformanceState.HIGH]: {
      label: null,
      title: 'High Usable Energy',
      color: StableSeverityColors.accent,
      severity: StatusSeverity.Normal,
    },
    [PerformanceState.MEDIUM]: {
      label: null,
      title: 'Medium Usable Energy',
      color: ModerateSeverityColors.accent,
      severity: StatusSeverity.Medium,
    },
    [PerformanceState.LOW]: {
      label: null,
      title: 'Low Usable Energy',
      color: CriticalSeverityColors.accent,
      severity: StatusSeverity.Critical,
    },
  },
  health: {
    [SpreadState.HIGH]: { label: null, color: CriticalSeverityColors.accent },
    [SpreadState.MEDIUM]: { label: null, color: ModerateSeverityColors.accent },
    [SpreadState.STABLE]: { label: 'Normal', color: StableSeverityColors.accent },
  },
  rte: {
    [PerformanceState.LOW]: {
      label: null,
      title: 'Low Round Trip Efficiency',
      color: CriticalSeverityColors.accent,
      severity: StatusSeverity.Critical,
    },
    [PerformanceState.MEDIUM]: {
      label: null,
      title: 'Medium Round Trip Efficiency',
      color: ModerateSeverityColors.accent,
      severity: StatusSeverity.Medium,
    },
    [PerformanceState.HIGH]: { label: 'Normal', color: StableSeverityColors.accent, severity: StatusSeverity.Normal },
  },
  risk: {
    [RiskState.HIGH]: { label: 'High', color: CriticalSeverityColors.accent, severity: StatusSeverity.Critical },
    [RiskState.MEDIUM]: { label: 'Medium', color: ModerateSeverityColors.accent, severity: StatusSeverity.Medium },
    [RiskState.NORMAL]: { label: 'Normal', color: StableSeverityColors.accent, severity: StatusSeverity.Normal },
  },
  warrantyState: {
    [WarrantyState.Ok]: {
      label: 'Operation within conditions',
      color: StableSeverityColors.accent,
      severity: StatusSeverity.Normal,
    },
    [WarrantyState.Breached]: {
      label: 'Threshold exceeded',
      color: CriticalSeverityColors.accent,
      severity: StatusSeverity.Critical,
    },
  },
};

export const onBoardingInProgress: StatsValuesConfigInterface = {
  label: 'Onboarding in Progress',
  color: '#B4B7BE',
  icon: 'settings',
};

export const featureNotAvailable: StatsValuesConfigInterface = {
  label: 'Feature not available',
  color: '#B4B7BE',
  icon: 'lock',
  isFeatureUnavailable: true,
};
